import Magnifier from "@/Icons/Magnifier";
import { LoadingIndicator } from "@/Icons/loadingIndicator";
import { trpc } from "@/Utils/trpc";
import { useNavigate } from "@/Utils/useNavigate";
import { useTrans } from "@/translations";

import React, { useCallback, useEffect, useRef, useState } from "react";

import { debounce } from "throttle-debounce";

import { NormalizedSearchResult, SearchDataType } from "./types";
import { labelKey, normalizeSearchData, urlToResult } from "./utils";

const Search: React.FC = () => {
  const { t } = useTrans();
  const [query, setQuery] = useState<string | undefined>("");
  const [selectedPoint, setSelectedPoint] = useState<
    NormalizedSearchResult | undefined
  >(undefined);
  const navigate = useNavigate();

  const queryEnabled = !!query?.length;

  const { data } = trpc.search.search.useQuery(
    {
      value: query!,
    },
    {
      enabled: queryEnabled,
    },
  );

  const { data: ancestry, isLoading: _isLoadingAncestry } =
    trpc.accessPoints.getWithAncestry.useQuery(
      {
        id: selectedPoint?.pk ?? "",
      },
      {
        enabled: selectedPoint !== undefined,
      },
    );

  const isLoadingAncestry = _isLoadingAncestry && selectedPoint !== undefined;

  const normalizedData = normalizeSearchData(data ?? {});

  const updateQueryDebounced = useRef(
    debounce(300, (val?: string) => {
      setQuery(val);
    }),
  );

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      updateQueryDebounced.current(e.target.value || undefined);
    },
    [updateQueryDebounced],
  );

  const onClick = useCallback(
    async (result: NormalizedSearchResult) => {
      if (result.type === SearchDataType.POINT_NAME) {
        setSelectedPoint(result);
      } else {
        const url = urlToResult(result);
        navigate(url);
        setQuery(undefined);
        setSelectedPoint(undefined);
      }
    },
    [navigate],
  );

  useEffect(() => {
    if (selectedPoint && ancestry) {
      const url = urlToResult(selectedPoint!, ancestry);
      navigate(url);
      setQuery(undefined);
      setSelectedPoint(undefined);
    }
  }, [ancestry, navigate, selectedPoint]);

  return (
    <div className="relative w-80 min-w-40">
      {isLoadingAncestry ? (
        <div className="flex h-10 w-full items-center border border-black px-2.5 py-2">
          <LoadingIndicator />
        </div>
      ) : (
        <>
          <div className="flex h-7 items-center gap-2 border border-black px-2 py-5">
            <div className="h-5 w-5">
              <Magnifier className="h-full w-full" />
            </div>
            <input
              className="placeholder:text-grey6 w-full border-none bg-transparent"
              type="text"
              placeholder={t("genericLabels:searchbarPlaceholder")}
              onChange={onChange}
            />
          </div>
          {queryEnabled && normalizedData.length > 0 && !isLoadingAncestry && (
            <ul className="absolute right-0 left-0 z-10 mt-1 max-h-[calc(90vh-50px)] overflow-scroll border border-gray-300 bg-white">
              {normalizedData?.map((result, index) => (
                <li
                  key={index}
                  className="p-2 hover:bg-gray-200"
                  onClick={() => onClick(result)}
                >
                  <div className="font-bold">
                    {t(`entities:${labelKey(result)}`)}
                  </div>
                  <div className="text-sm">{result.value}</div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
};

export default Search;
